import type { CurrentUserOrgDTO, JsonObject } from '@/app-package';
import type { PortalAnsweringConfig } from 'api/AnsweringSubscriptionController/getPortalAnsweringConfig';
import { isLegacyAnsweringProvider } from 'api/AnsweringServiceController';

/**
 * Checks if the Answering Service org (either from the MLS
 * Onboarding Portal or from the individual Brokerage settings)
 * is using one of the Answering Service orgs who
 * provide the legacy Answering Service instead of modern
 * Appointment Desk & Live Concierge Service.
 *
 * @param answeringPortalConfig the Onboarding Portal settings for whatever MLS is above this user's org. Grab it from the auth object in AuthContext.
 * @param org the org object for a user's brokerage. Grab it from the auth object in AuthContext.
 * @returns boolean whether this org's Answering Service provider is for legacy Answering Service.
 */
const usesLegacyServiceProvider = async (
  answeringPortalConfig: PortalAnsweringConfig,
  org: JsonObject<CurrentUserOrgDTO>,
): Promise<boolean> => {
  // The case where we have no Answering Service provider ID stored anywhere
  const answeringServiceIdMissing =
    !answeringPortalConfig?.answeringServiceId && !org?.answeringServiceOrg;

  // The MLS uses a legacy provider
  const onboardingPortalUsesLegacy =
    answeringPortalConfig?.answeringServiceId &&
    (await isLegacyAnsweringProvider({
      answeringOrgId: answeringPortalConfig?.answeringServiceId,
    }));

  // The brokerage uses a legacy provider
  const orgUsesLegacy =
    org?.answeringServiceOrg &&
    (await isLegacyAnsweringProvider({
      answeringOrgId: org?.answeringServiceOrg,
    }));

  // Validate
  if (
    answeringServiceIdMissing ||
    onboardingPortalUsesLegacy ||
    orgUsesLegacy
  ) {
    return true;
  }
  return false;
};

export default usesLegacyServiceProvider;
